import axios from 'axios';
import { Dive, AuthResponse } from '../types/Dive';
import { DemoService } from './demoService';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authService = {
  getAuthUrl: async (redirectUri: string): Promise<AuthResponse> => {
    try {
      const response = await apiClient.get(`/dives/auth/url?redirectUri=${encodeURIComponent(redirectUri)}`);
      return response.data;
    } catch (error) {
      console.error('Error getting auth URL:', error);
      throw error;
    }
  },

  exchangeToken: async (code: string, redirectUri: string): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post('/dives/auth/token', {
        code,
        redirectUri,
      });
      return response.data;
    } catch (error) {
      console.error('Error exchanging token:', error);
      throw error;
    }
  },
};

export const diveService = {
  getDives: async (accessToken: string, limit: number = 1000): Promise<Dive[]> => {
    // Check if we're in demo mode
    if (DemoService.isDemoMode(accessToken)) {
      return DemoService.getDemoDives(limit);
    }

    try {
      const response = await apiClient.get('/dives', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: { limit },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching workout activities:', error);
      throw error;
    }
  },

  getDive: async (diveId: string, accessToken: string): Promise<Dive> => {
    // Check if we're in demo mode
    if (DemoService.isDemoMode(accessToken)) {
      return DemoService.getDemoDive(diveId);
    }

    try {
      const response = await apiClient.get(`/dives/${diveId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching dive:', error);
      throw error;
    }
  },

  getLocationUpdates: async (diveIds: string[]): Promise<{ updates: Array<{ diveId: string; location: string; updated: boolean }> }> => {
    try {
      if (diveIds.length === 0) {
        return { updates: [] };
      }
      
      const response = await apiClient.get('/dives/location-updates', {
        params: {
          diveIds: diveIds,
        },
        paramsSerializer: {
          indexes: null, // This will create ?diveIds=id1&diveIds=id2 format
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching location updates:', error);
      throw error;
    }
  },

  // REMOVED: geocodeDiveLocation now handled by frontend geocodingService.ts
  // This eliminates backend API calls and reduces AWS costs

  getDetailedDiveData: async (diveId: string, accessToken: string): Promise<any> => {
    // Check if we're in demo mode
    if (DemoService.isDemoMode(accessToken)) {
      // Return enhanced demo data with simulated FIT file data
      const demoDive = await DemoService.getDemoDive(diveId);
      // Generate realistic demo data that matches the FIT parser structure
      const startPressureBar = 200 + Math.random() * 50; // 200-250 bar
      const endPressureBar = startPressureBar - (80 + Math.random() * 60); // Consume 80-140 bar
      const diveTimeMinutes = 35 + Math.random() * 20;
      
      return {
        workoutKey: diveId,
        basicDetails: demoDive,
        fitFileData: {
          fileSize: 25600,
          isValid: true,
          parsedTimestamp: new Date().toISOString(),
          sessionStartTime: new Date(Date.now() - diveTimeMinutes * 60000).toISOString(),
          sessionEndTime: new Date().toISOString(),
          totalDuration: { totalMinutes: diveTimeMinutes },
          totalDistance: Math.round(50 + Math.random() * 150),
          depthData: {
            maxDepth: demoDive.maxDepth,
            minDepth: 0.5 + Math.random() * 2,
            avgDepth: demoDive.avgDepth,
            depthReadings: Array.from({length: 50}, (_, i) => ({
              timestamp: new Date(Date.now() - (50-i) * 60000).toISOString(),
              depth: demoDive.maxDepth * (0.3 + 0.7 * Math.sin(i * Math.PI / 25))
            }))
          },
          temperatureData: {
            maxTemperature: demoDive.waterTemperature + 2,
            minTemperature: demoDive.waterTemperature - 1,
            avgTemperature: demoDive.waterTemperature,
            temperatureReadings: Array.from({length: 30}, (_, i) => ({
              timestamp: new Date(Date.now() - (30-i) * 80000).toISOString(),
              temperature: demoDive.waterTemperature + (Math.random() - 0.5) * 2
            }))
          },
          heartRateData: {
            maxHeartRate: 140 + Math.round(Math.random() * 30),
            minHeartRate: 80 + Math.round(Math.random() * 20),
            avgHeartRate: 110 + Math.round(Math.random() * 20),
            heartRateReadings: Array.from({length: 40}, (_, i) => ({
              timestamp: new Date(Date.now() - (40-i) * 60000).toISOString(),
              heartRate: 110 + Math.round((Math.random() - 0.5) * 40)
            }))
          },
          pressureReadings: Array.from({length: 20}, (_, i) => ({
            timestamp: new Date(Date.now() - (20-i) * 120000).toISOString(),
            pressure: startPressureBar - (startPressureBar - endPressureBar) * (i / 19)
          })),
          diveSettings: {
            gasType: "Air (21% O2)",
            waterType: Math.random() > 0.5 ? "Salt Water" : "Fresh Water",
            conservatismFactor: Math.round(Math.random() * 4)
          },
          gasData: [
            { gasName: "Air", oxygenPercent: 21.0, heliumPercent: 0.0 }
          ]
        },
        sacData: {
          sacRate: `${(15 + Math.random() * 10).toFixed(1)} L/min`,
          airConsumption: `${(startPressureBar - endPressureBar).toFixed(0)} bar consumed`,
          method: "Calculated from demo tank pressure + depth profile",
          tankStartPressure: startPressureBar,
          tankEndPressure: endPressureBar,
          averageDepth: demoDive.avgDepth,
          maxDepth: demoDive.maxDepth,
          diveTimeMinutes: diveTimeMinutes,
          depthFactor: (demoDive.avgDepth / 10.0) + 1
        },
        dataSources: ['DemoMode', 'SimulatedFitFile']
      };
    }

    try {
      const response = await apiClient.get(`/dives/${diveId}/detailed`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching detailed dive data:', error);
      throw error;
    }
  },

  downloadFitFile: async (diveId: string, accessToken: string): Promise<void> => {
    // Check if we're in demo mode - can't download real FIT files in demo mode
    if (DemoService.isDemoMode(accessToken)) {
      alert('FIT file download is not available in demo mode. Please authenticate with Suunto to download real FIT files.');
      return;
    }

    try {
      const response = await apiClient.get(`/dives/${diveId}/fit`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: 'blob', // Important for binary data
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dive_${diveId}.fit`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading FIT file:', error);
      throw error;
    }
  },

  downloadFitFileAsText: async (diveId: string, accessToken: string): Promise<void> => {
    // Check if we're in demo mode - can't download real FIT files in demo mode
    if (DemoService.isDemoMode(accessToken)) {
      alert('FIT file text analysis is not available in demo mode. Please authenticate with Suunto to analyze real FIT files.');
      return;
    }

    try {
      const response = await apiClient.get(`/dives/${diveId}/fit-text`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: 'blob', // Important for text data
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dive_${diveId}_analysis.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading FIT file text analysis:', error);
      throw error;
    }
  },

  updateDive: async (diveId: string, accessToken: string, updates: Partial<Dive>): Promise<Dive> => {
    // Check if we're in demo mode - can't update dives in demo mode
    if (DemoService.isDemoMode(accessToken)) {
      console.warn('Dive updates are not persisted in demo mode');
      // Return a mock updated dive for demo purposes
      const demoDive = await DemoService.getDemoDive(diveId);
      return { ...demoDive, ...updates };
    }

    try {
      // Convert Dive object to UpdateDiveRequest format
      const updateRequest: any = {};

      if (updates.startTime) updateRequest.StartTime = updates.startTime;
      if (updates.maxDepth !== undefined) updateRequest.MaxDepth = updates.maxDepth;
      if (updates.avgDepth !== undefined) updateRequest.AvgDepth = updates.avgDepth;
      if (updates.location) updateRequest.Location = updates.location;
      if (updates.waterTemperature !== undefined) updateRequest.WaterTemperature = updates.waterTemperature;
      if (updates.diveType) updateRequest.DiveType = updates.diveType;
      if (updates.latitude !== undefined) updateRequest.Latitude = updates.latitude;
      if (updates.longitude !== undefined) updateRequest.Longitude = updates.longitude;

      // Handle additional data
      if (updates.additionalData) {
        if (updates.additionalData.equipmentIds) {
          updateRequest.EquipmentIds = updates.additionalData.equipmentIds;
        }
        if (updates.additionalData.weightUsed !== undefined) {
          updateRequest.WeightUsed = updates.additionalData.weightUsed;
        }
        if (updates.additionalData.weightUnit) {
          updateRequest.WeightUnit = updates.additionalData.weightUnit;
        }
      }

      const response = await apiClient.put(`/dives/my-dives/${diveId}`, updateRequest, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return response.data.dive;
    } catch (error) {
      console.error('Error updating dive:', error);
      throw error;
    }
  },
};